/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Report Service
 *
 * Client-side service for fetching and managing progress reports.
 * Supports PDF generation and download functionality.
 */

import api from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('reportService')

export const REPORT_TYPES = {
  STUDENT_PROGRESS: 'STUDENT_PROGRESS',
  CLASS_SUMMARY: 'CLASS_SUMMARY',
  PARENT_REPORT: 'PARENT_REPORT',
  SCHOOL_REPORT: 'SCHOOL_REPORT'
}

export const REPORT_PERIODS = {
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  ALL_TIME: 'all_time'
}

export const REPORT_STATUS = {
  PENDING: 'PENDING',
  GENERATING: 'GENERATING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED'
}

const reportService = {
  /**
   * Get available report types for current user
   */
  async getReportTypes() {
    try {
      const response = await api.get('/reports/types')
      return response.data
    } catch (error) {
      logger.error('Failed to fetch report types:', error)
      throw error
    }
  },

  /**
   * Get current user's report
   */
  async getMyReport(options = {}) {
    try {
      const params = new URLSearchParams()
      if (options.period) params.append('period', options.period)
      if (options.format) params.append('format', options.format)
      if (options.savePdf !== undefined) params.append('savePdf', options.savePdf)

      const queryString = params.toString()
      const url = `/reports/me${queryString ? `?${queryString}` : ''}`

      const response = await api.get(url)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch my report:', error)
      throw error
    }
  },

  /**
   * Get student progress report
   */
  async getStudentReport(studentId, options = {}) {
    try {
      const params = new URLSearchParams()
      if (options.period) params.append('period', options.period)
      if (options.startDate) params.append('startDate', options.startDate)
      if (options.endDate) params.append('endDate', options.endDate)
      if (options.format) params.append('format', options.format)
      if (options.savePdf !== undefined) params.append('savePdf', options.savePdf)

      const queryString = params.toString()
      const url = `/reports/student/${studentId}${queryString ? `?${queryString}` : ''}`

      const response = await api.get(url)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch student report:', error)
      throw error
    }
  },

  /**
   * Get class summary report (instructor/admin only)
   */
  async getClassReport(classId, options = {}) {
    try {
      const params = new URLSearchParams()
      if (options.period) params.append('period', options.period)
      if (options.startDate) params.append('startDate', options.startDate)
      if (options.endDate) params.append('endDate', options.endDate)
      if (options.format) params.append('format', options.format)
      if (options.savePdf !== undefined) params.append('savePdf', options.savePdf)

      const queryString = params.toString()
      const url = `/reports/class/${classId}${queryString ? `?${queryString}` : ''}`

      const response = await api.get(url)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch class report:', error)
      throw error
    }
  },

  /**
   * Get user's report history
   */
  async getReportHistory(options = {}) {
    try {
      const params = new URLSearchParams()
      if (options.type) params.append('type', options.type)
      if (options.status) params.append('status', options.status)
      if (options.page) params.append('page', options.page)
      if (options.limit) params.append('limit', options.limit)

      const queryString = params.toString()
      const url = `/reports/history${queryString ? `?${queryString}` : ''}`

      const response = await api.get(url)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch report history:', error)
      throw error
    }
  },

  /**
   * Check report generation status
   */
  async getReportStatus(reportId) {
    try {
      const response = await api.get(`/reports/${reportId}/status`)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch report status:', error)
      throw error
    }
  },

  /**
   * Download report as PDF
   * Returns the PDF data as a Blob for download
   */
  async downloadReportPdf(reportId) {
    try {
      const response = await api.get(`/reports/${reportId}/download`, {
        responseType: 'blob'
      })

      // Debug: Log response details (only in development)
      if (import.meta.env.DEV) {
        logger.debug('PDF Download Response:', {
          status: response.status,
          contentType: response.headers['content-type'],
          contentLength: response.headers['content-length'],
          dataType: response.data?.constructor?.name,
          dataSize: response.data?.size
        })
      }

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition']
      let filename = `report-${reportId}.pdf`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/i)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      // Ensure the blob has the correct MIME type
      let blob = response.data
      if (blob && blob.type !== 'application/pdf') {
        if (import.meta.env.DEV) {
          logger.warn('Blob type is not application/pdf, creating new blob with correct type')
        }
        blob = new Blob([response.data], { type: 'application/pdf' })
      }

      // Validate PDF header by reading first bytes (only in development)
      if (import.meta.env.DEV && blob && blob.size > 0) {
        const headerSlice = blob.slice(0, 5)
        const headerText = await headerSlice.text()
        logger.debug(
          'PDF Header check:',
          headerText,
          headerText === '%PDF-' ? '✓ Valid' : '✗ Invalid'
        )
        if (headerText !== '%PDF-') {
          logger.error('Downloaded file does not appear to be a valid PDF')
        }
      }

      return {
        blob,
        filename
      }
    } catch (error) {
      logger.error('Failed to download report PDF:', error)
      throw error
    }
  },

  /**
   * Generate and download student report as PDF
   * This is a convenience method that generates and downloads in one step
   */
  async generateAndDownloadStudentReport(studentId, options = {}) {
    try {
      // First generate the report with PDF saving
      const reportResponse = await this.getStudentReport(studentId, {
        ...options,
        savePdf: true
      })

      // API returns { success, data: { reportId, status, ... } }
      const reportData = reportResponse.data || reportResponse
      if (!reportData.reportId) {
        throw new Error('Report generation did not return a report ID')
      }

      // Poll for completion if status is not COMPLETED
      let status = reportData.status
      let attempts = 0
      const maxAttempts = 30 // 30 seconds max wait

      while (status !== REPORT_STATUS.COMPLETED && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        const statusResponse = await this.getReportStatus(reportData.reportId)
        const statusData = statusResponse.data || statusResponse
        status = statusData.status

        if (status === REPORT_STATUS.FAILED) {
          throw new Error(statusData.error || 'Report generation failed')
        }

        attempts++
      }

      if (status !== REPORT_STATUS.COMPLETED) {
        throw new Error('Report generation timed out')
      }

      // Download the PDF
      return await this.downloadReportPdf(reportData.reportId)
    } catch (error) {
      logger.error('Failed to generate and download student report:', error)
      throw error
    }
  },

  /**
   * Generate and download class report as PDF
   */
  async generateAndDownloadClassReport(classId, options = {}) {
    try {
      const reportResponse = await this.getClassReport(classId, {
        ...options,
        savePdf: true
      })

      // API returns { success, data: { reportId, status, ... } }
      const reportData = reportResponse.data || reportResponse
      if (!reportData.reportId) {
        throw new Error('Report generation did not return a report ID')
      }

      // Poll for completion
      let status = reportData.status
      let attempts = 0
      const maxAttempts = 60 // 60 seconds for class reports (larger)

      while (status !== REPORT_STATUS.COMPLETED && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        const statusResponse = await this.getReportStatus(reportData.reportId)
        const statusData = statusResponse.data || statusResponse
        status = statusData.status

        if (status === REPORT_STATUS.FAILED) {
          throw new Error(statusData.error || 'Report generation failed')
        }

        attempts++
      }

      if (status !== REPORT_STATUS.COMPLETED) {
        throw new Error('Report generation timed out')
      }

      return await this.downloadReportPdf(reportData.reportId)
    } catch (error) {
      logger.error('Failed to generate and download class report:', error)
      throw error
    }
  },

  /**
   * Trigger browser download of a PDF blob
   */
  triggerDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)

    try {
      link.click()
    } finally {
      // Ensure cleanup happens even if click fails
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  },

  /**
   * Generate my report and download as PDF
   */
  async generateAndDownloadMyReport(options = {}) {
    try {
      const reportResponse = await this.getMyReport({
        ...options,
        savePdf: true
      })

      // API returns { success, data: { reportId, status, ... } }
      const reportData = reportResponse.data || reportResponse
      if (!reportData.reportId) {
        throw new Error('Report generation did not return a report ID')
      }

      // Poll for completion
      let status = reportData.status
      let attempts = 0
      const maxAttempts = 30

      while (status !== REPORT_STATUS.COMPLETED && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        const statusResponse = await this.getReportStatus(reportData.reportId)
        const statusData = statusResponse.data || statusResponse
        status = statusData.status

        if (status === REPORT_STATUS.FAILED) {
          throw new Error(statusData.error || 'Report generation failed')
        }

        attempts++
      }

      if (status !== REPORT_STATUS.COMPLETED) {
        throw new Error('Report generation timed out')
      }

      return await this.downloadReportPdf(reportData.reportId)
    } catch (error) {
      logger.error('Failed to generate and download my report:', error)
      throw error
    }
  }
}

export default reportService
